import { Levels } from '../../../../../typings/api/skymap/rest/v0/.common';
import { TileCoord } from '../ortho-tile-asset/ortho-tile-typings';

/** GDAL COGs keep every IFD and tile index before the image data, so one fetch usually covers it. */
const INITIAL_HEADER_BYTES = 64 * 1024;

const Tag = {
  NewSubfileType: 254,
  ImageWidth: 256,
  ImageLength: 257,
  Compression: 259,
  TileWidth: 322,
  TileLength: 323,
  TileOffsets: 324,
  TileByteCounts: 325,
  JPEGTables: 347,
  ModelPixelScale: 33550,
  ModelTiepoint: 33922,
  GeoKeyDirectory: 34735,
  GdalMetadata: 42112,
} as const;

/** GDAL metadata item our pipeline writes with the proj4 definition of the COG's CRS. */
const PROJ4_METADATA_ITEM = 'SKYMAP_PROJ4';

const GeoKey = {
  RasterType: 1025,
  GeographicType: 2048,
  ProjectedCSType: 3072,
} as const;

const SUBFILE_TYPE_MASK = 4;
const RASTER_PIXEL_IS_POINT = 2;

export const Compression = {
  None: 1,
  Jpeg: 7,
  Deflate: 8,
  AdobeDeflate: 32946,
} as const;

/** Byte size of each TIFF field type. */
const TYPE_SIZES: Record<number, number> = {
  1: 1, // BYTE
  2: 1, // ASCII
  3: 2, // SHORT
  4: 4, // LONG
  5: 8, // RATIONAL
  6: 1, // SBYTE
  7: 1, // UNDEFINED
  8: 2, // SSHORT
  9: 4, // SLONG
  10: 8, // SRATIONAL
  11: 4, // FLOAT
  12: 8, // DOUBLE
  16: 8, // LONG8
  17: 8, // SLONG8
  18: 8, // IFD8
};

function unescapeXml(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

/** Thrown when the header buffer is too small; carries how many bytes are needed. */
class HeaderOutOfRangeError extends Error {
  constructor(readonly needed: number) {
    super(`COG header needs ${needed} bytes`);
  }
}

type IfdEntry = { type: number; count: number; valueOffset: number };

/**
 * Minimal TIFF/BigTIFF IFD reader over a prefix of the file.
 * Values are read lazily so tags we don't use (e.g. GDAL metadata) never need to be in the buffer.
 */
class TiffHeader {
  private readonly view: DataView;
  private readonly littleEndian: boolean;
  private readonly bigTiff: boolean;
  readonly ifds: Map<number, IfdEntry>[] = [];

  constructor(buffer: ArrayBuffer) {
    this.view = new DataView(buffer);
    const byteOrder = this.view.getUint16(0, false);
    if (byteOrder !== 0x4949 && byteOrder !== 0x4d4d) {
      throw new Error('Not a TIFF file');
    }
    this.littleEndian = byteOrder === 0x4949;

    const magic = this.view.getUint16(2, this.littleEndian);
    if (magic !== 42 && magic !== 43) {
      throw new Error(`Unknown TIFF magic ${magic}`);
    }
    this.bigTiff = magic === 43;

    let ifdOffset = this.bigTiff ? this.uint64(8) : this.uint32(4);
    while (ifdOffset) {
      ifdOffset = this.readIfd(ifdOffset);
    }
  }

  numbers(ifd: Map<number, IfdEntry>, tag: number): number[] | undefined {
    const entry = ifd.get(tag);
    if (!entry) {
      return undefined;
    }
    const size = TYPE_SIZES[entry.type];
    this.ensure(entry.valueOffset + entry.count * size);

    const out = new Array<number>(entry.count);
    for (let i = 0; i < entry.count; i++) {
      const p = entry.valueOffset + i * size;
      switch (entry.type) {
        case 1:
        case 7:
          out[i] = this.view.getUint8(p);
          break;
        case 3:
          out[i] = this.view.getUint16(p, this.littleEndian);
          break;
        case 4:
          out[i] = this.uint32(p);
          break;
        case 11:
          out[i] = this.view.getFloat32(p, this.littleEndian);
          break;
        case 12:
          out[i] = this.view.getFloat64(p, this.littleEndian);
          break;
        case 16:
        case 18:
          out[i] = this.uint64(p);
          break;
        default:
          throw new Error(`Unsupported TIFF field type ${entry.type} for tag ${tag}`);
      }
    }
    return out;
  }

  number(ifd: Map<number, IfdEntry>, tag: number): number | undefined {
    return this.numbers(ifd, tag)?.[0];
  }

  bytes(ifd: Map<number, IfdEntry>, tag: number): Uint8Array | undefined {
    const entry = ifd.get(tag);
    if (!entry) {
      return undefined;
    }
    this.ensure(entry.valueOffset + entry.count);
    // Copy so the (possibly large) header buffer can be garbage collected
    return new Uint8Array(
      this.view.buffer.slice(entry.valueOffset, entry.valueOffset + entry.count),
    );
  }

  /** Reads one IFD and returns the offset of the next (0 when last). */
  private readIfd(offset: number): number {
    const countSize = this.bigTiff ? 8 : 2;
    const entrySize = this.bigTiff ? 20 : 12;
    const inlineSize = this.bigTiff ? 8 : 4;

    this.ensure(offset + countSize);
    const n = this.bigTiff ? this.uint64(offset) : this.view.getUint16(offset, this.littleEndian);
    const entriesStart = offset + countSize;
    this.ensure(entriesStart + n * entrySize + inlineSize);

    const ifd = new Map<number, IfdEntry>();
    for (let i = 0; i < n; i++) {
      const p = entriesStart + i * entrySize;
      const tag = this.view.getUint16(p, this.littleEndian);
      const type = this.view.getUint16(p + 2, this.littleEndian);
      const count = this.bigTiff ? this.uint64(p + 4) : this.uint32(p + 4);
      const valuePos = p + (this.bigTiff ? 12 : 8);
      const size = (TYPE_SIZES[type] ?? 1) * count;
      const valueOffset =
        size <= inlineSize
          ? valuePos
          : this.bigTiff
            ? this.uint64(valuePos)
            : this.uint32(valuePos);
      ifd.set(tag, { type, count, valueOffset });
    }
    this.ifds.push(ifd);

    const next = entriesStart + n * entrySize;
    return this.bigTiff ? this.uint64(next) : this.uint32(next);
  }

  private ensure(end: number): void {
    if (end > this.view.byteLength) {
      throw new HeaderOutOfRangeError(end);
    }
  }

  private uint32(p: number): number {
    return this.view.getUint32(p, this.littleEndian);
  }

  private uint64(p: number): number {
    return Number(this.view.getBigUint64(p, this.littleEndian));
  }
}

type CogLevel = {
  /** Quadtree zoom: 0 is the coarsest overview, maxZoom is full resolution. */
  z: number;
  width: number;
  height: number;
  tilesAcross: number;
  tilesDown: number;
  /**
   * CRS units per pixel. Overviews cover the full image extent, so odd sizes that GDAL rounds
   * (17055 px -> 8527) give a slightly larger pixel than an exact halving would.
   */
  resolutionX: number;
  resolutionY: number;
  tileOffsets: number[];
  tileByteCounts: number[];
  maskTileOffsets?: number[];
  maskTileByteCounts?: number[];
};

export type CogTile = {
  imageOffset: number;
  imageByteCount: number;
  maskOffset?: number;
  maskByteCount?: number;
  /** Pixels of the tile that are inside the image; edge tiles are padded to the full tile size. */
  validWidth: number;
  validHeight: number;
};

export type CogBounds = { minX: number; minY: number; maxX: number; maxY: number };

/**
 * A tiled, JPEG-compressed Cloud Optimized GeoTIFF with an optional 1-bit deflate mask per level.
 * The overviews must halve in size each level, which makes the pyramid a quadtree that
 * OrthoTileAsset can traverse with ordinary z/x/y tile coordinates.
 */
export class CogSource {
  readonly tileWidth: number;
  readonly tileHeight: number;
  readonly maskCompression: number | undefined;
  /** Shared JPEG quantization/Huffman tables (complete JPEG stream with SOI/EOI). */
  readonly jpegTables: Uint8Array | undefined;
  /** EPSG code from the GeoKeys, if present. */
  readonly epsg: number | undefined;
  /** proj4 definition embedded in the GDAL metadata, if present. */
  readonly proj4: string | undefined;
  /** Upper-left corner of the image in the COG's CRS. */
  readonly originX: number;
  readonly originY: number;
  private readonly levels: CogLevel[];

  private constructor(
    readonly url: string,
    header: TiffHeader,
  ) {
    const imageIfds: Map<number, IfdEntry>[] = [];
    const maskIfds: Map<number, IfdEntry>[] = [];
    for (const ifd of header.ifds) {
      const subfileType = header.number(ifd, Tag.NewSubfileType) ?? 0;
      (subfileType & SUBFILE_TYPE_MASK ? maskIfds : imageIfds).push(ifd);
    }
    if (!imageIfds.length) {
      throw new Error('COG has no image IFDs');
    }

    const width = (ifd: Map<number, IfdEntry>) => header.number(ifd, Tag.ImageWidth)!;
    const height = (ifd: Map<number, IfdEntry>) => header.number(ifd, Tag.ImageLength)!;
    imageIfds.sort((a, b) => width(b) - width(a));

    const full = imageIfds[0];
    this.tileWidth = header.number(full, Tag.TileWidth) ?? 0;
    this.tileHeight = header.number(full, Tag.TileLength) ?? 0;
    if (!this.tileWidth || !this.tileHeight) {
      throw new Error('COG must be tiled');
    }
    const compression = header.number(full, Tag.Compression);
    if (compression !== Compression.Jpeg) {
      throw new Error(`Only JPEG compressed COGs are supported, got compression ${compression}`);
    }
    this.jpegTables = header.bytes(full, Tag.JPEGTables);

    const [scaleX, scaleY] = header.numbers(full, Tag.ModelPixelScale) ?? [];
    const tiepoint = header.numbers(full, Tag.ModelTiepoint) ?? [];
    if (scaleX === undefined || scaleY === undefined || tiepoint.length < 6) {
      throw new Error('COG is missing ModelPixelScale/ModelTiepoint');
    }
    const geoKeys = this.parseGeoKeys(header.numbers(full, Tag.GeoKeyDirectory));
    this.epsg = geoKeys.get(GeoKey.ProjectedCSType) ?? geoKeys.get(GeoKey.GeographicType);
    this.proj4 = this.parseGdalMetadata(header.bytes(full, Tag.GdalMetadata)).get(
      PROJ4_METADATA_ITEM,
    );

    const [i, j, , x, y] = tiepoint;
    let originX = x - i * scaleX;
    let originY = y + j * scaleY;
    if (geoKeys.get(GeoKey.RasterType) === RASTER_PIXEL_IS_POINT) {
      originX -= scaleX / 2;
      originY += scaleY / 2;
    }
    this.originX = originX;
    this.originY = originY;

    const maxZoom = imageIfds.length - 1;
    this.levels = imageIfds
      .map((ifd, index): CogLevel => {
        const w = width(ifd);
        const h = height(ifd);
        const factor = 2 ** index;
        // GDAL rounds odd overview sizes up or down, so each size is within 1 px of full / factor
        if (Math.abs(w - width(full) / factor) >= 1 || Math.abs(h - height(full) / factor) >= 1) {
          throw new Error(`COG overview ${index} is not a power-of-two reduction (${w}x${h})`);
        }
        if (
          header.number(ifd, Tag.TileWidth) !== this.tileWidth ||
          header.number(ifd, Tag.TileLength) !== this.tileHeight
        ) {
          throw new Error(`COG overview ${index} uses a different tile size`);
        }

        const mask = maskIfds.find((m) => width(m) === w && height(m) === h);
        return {
          z: maxZoom - index,
          width: w,
          height: h,
          tilesAcross: Math.ceil(w / this.tileWidth),
          tilesDown: Math.ceil(h / this.tileHeight),
          resolutionX: (scaleX * width(full)) / w,
          resolutionY: (scaleY * height(full)) / h,
          tileOffsets: header.numbers(ifd, Tag.TileOffsets)!,
          tileByteCounts: header.numbers(ifd, Tag.TileByteCounts)!,
          maskTileOffsets: mask && header.numbers(mask, Tag.TileOffsets),
          maskTileByteCounts: mask && header.numbers(mask, Tag.TileByteCounts),
        };
      })
      .reverse();

    this.maskCompression = maskIfds.length
      ? header.number(maskIfds[0], Tag.Compression)
      : undefined;
  }

  /**
   * Fetches and parses the header. Starts with a 64 KB range and grows it if the IFDs or
   * tile index arrays reach further into the file.
   */
  static async open(url: string, signal?: AbortSignal): Promise<CogSource> {
    let size = INITIAL_HEADER_BYTES;
    for (;;) {
      const response = await fetch(url, { headers: { Range: `bytes=0-${size - 1}` }, signal });
      if (!response.ok) {
        throw new Error(`COG header fetch failed: ${response.status} ${response.statusText}`);
      }
      const buffer = await response.arrayBuffer();
      try {
        return new CogSource(url, new TiffHeader(buffer));
      } catch (err) {
        // A short buffer means we already have the whole file, so retrying won't help
        if (err instanceof HeaderOutOfRangeError && buffer.byteLength >= size) {
          size = Math.max(err.needed, size * 2);
          continue;
        }
        throw err;
      }
    }
  }

  get maxZoom(): number {
    return this.levels.length - 1;
  }

  /** Tile grid per zoom, in the shape OrthoTileAsset's LevelIndex expects. */
  getLevels(): Levels[] {
    return this.levels.map((l) => ({
      zoom: l.z,
      columnMin: 0,
      columnMax: l.tilesAcross - 1,
      rowMin: 0,
      rowMax: l.tilesDown - 1,
    }));
  }

  getTile(coord: TileCoord): CogTile | undefined {
    const level = this.levels[coord.z];
    if (!level || coord.x >= level.tilesAcross || coord.y >= level.tilesDown) {
      return undefined;
    }
    const index = coord.y * level.tilesAcross + coord.x;
    return {
      imageOffset: level.tileOffsets[index],
      imageByteCount: level.tileByteCounts[index],
      maskOffset: level.maskTileOffsets?.[index],
      maskByteCount: level.maskTileByteCounts?.[index],
      validWidth: Math.min(this.tileWidth, level.width - coord.x * this.tileWidth),
      validHeight: Math.min(this.tileHeight, level.height - coord.y * this.tileHeight),
    };
  }

  /** Extent of the valid part of a tile, in the COG's CRS. */
  getTileBounds(coord: TileCoord): CogBounds {
    const level = this.levels[coord.z];
    const tile = this.getTile(coord);
    if (!tile) {
      throw new Error(`COG tile ${coord.z}/${coord.x}/${coord.y} is out of range`);
    }
    const left = coord.x * this.tileWidth;
    const top = coord.y * this.tileHeight;
    return {
      minX: this.originX + left * level.resolutionX,
      maxX: this.originX + (left + tile.validWidth) * level.resolutionX,
      maxY: this.originY - top * level.resolutionY,
      minY: this.originY - (top + tile.validHeight) * level.resolutionY,
    };
  }

  /** Extent of the whole image, in the COG's CRS. */
  getBounds(): CogBounds {
    const full = this.levels[this.maxZoom];
    return {
      minX: this.originX,
      maxX: this.originX + full.width * full.resolutionX,
      maxY: this.originY,
      minY: this.originY - full.height * full.resolutionY,
    };
  }

  /** Reads the default-domain items of GDAL's <GDALMetadata> XML. */
  private parseGdalMetadata(bytes: Uint8Array | undefined): Map<string, string> {
    const items = new Map<string, string>();
    if (!bytes) {
      return items;
    }
    const xml = new TextDecoder().decode(bytes);
    // Items with a domain or band attribute belong elsewhere, so only bare <Item name="..."> match
    for (const [, name, value] of xml.matchAll(/<Item name="([^"]*)">([^<]*)<\/Item>/g)) {
      items.set(name, unescapeXml(value));
    }
    return items;
  }

  private parseGeoKeys(directory: number[] | undefined): Map<number, number> {
    const keys = new Map<number, number>();
    if (!directory) {
      return keys;
    }
    const numberOfKeys = directory[3];
    for (let k = 0; k < numberOfKeys; k++) {
      const [keyId, location, , value] = directory.slice(4 + k * 4, 8 + k * 4);
      // location 0 means the value is stored inline; the keys we read are all inline shorts
      if (location === 0) {
        keys.set(keyId, value);
      }
    }
    return keys;
  }
}
